/* eslint-disable no-case-declarations */
import React from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import styles from './form.module.scss';

import Button from '../Button';
import FormTitle from './FormTitle';
import FormSubtitle from './FormSubtitle';
import FormLogo from './FormLogo';
import FormGroup from './FormGroup/FormGroup';
import FormItem from './FormItem/FormItem';
import {
  ValidatedEmailInput,
  ValidatedPasswordInput,
  ValidatedPromoInput,
} from '../ValidatedInput';
import TermsCheckbox from '../TermsCheckbox/TermsCheckbox';
import CountrySelect from '../CountrySelect';
import FormMessage from './FormMessage';
import BackButton from './FormBackButton';
import ValidatedSelectModalRadio from '../ValidatedSelectModalRadio';
import ValidatedPinInput from '../ValidatedPinInput';

const matchInput = item => {
  const { type, label, registerOptions, expand, position, ...rest } = item;
  switch (type) {
    case 'pin':
      return (
        <FormItem
          label={label}
          input={<ValidatedPinInput {...rest} />}
          registerOptions={registerOptions}
          expand={expand}
          position={position}
        />
      );
    case 'email':
      return (
        <FormItem
          label={label}
          input={<ValidatedEmailInput {...rest} />}
          registerOptions={registerOptions}
          expand={expand}
          position={position}
        />
      );
    case 'password':
      return (
        <FormItem
          label={label}
          input={<ValidatedPasswordInput {...rest} />}
          registerOptions={registerOptions}
          expand={expand}
          position={position}
        />
      );
    case 'promo':
      return (
        <FormItem
          label={label}
          input={<ValidatedPromoInput {...rest} />}
          registerOptions={registerOptions}
          expand={expand}
          position={position}
        />
      );
    case 'country':
      return (
        <FormItem
          useController
          label={label}
          input={<CountrySelect {...rest} />}
          expand={expand}
          position={position}
        />
      );
    case 'radioSelect':
      return (
        <FormItem
          label={label}
          input={<ValidatedSelectModalRadio {...rest} />}
          registerOptions={registerOptions}
          expand={expand}
          position={position}
        />
      );
    case 'check':
    case 'checbox':
      return (
        <FormItem
          input={<TermsCheckbox {...rest} />}
          registerOptions={registerOptions}
          expand={expand}
          position={position}
        />
      );
    default:
      return null;
  }
};

const randomKeycreator = () => {
  let counter = 0;
  return function(name) {
    counter += 1;
    return name + counter;
  };
};
const randomKey = randomKeycreator();

const renderElements = (
  elements = [],
  { handleSubmit, errors, clearErrors },
) => {
  if (elements instanceof Array) {
    return elements.map(element =>
      renderElements(element, { handleSubmit, errors, clearErrors }),
    );
  }

  if(!elements?.element) return null;

  const { element, ...rest } = elements;
  switch (element) {
    case 'backButton':
      return <BackButton {...rest} />;
    case 'logo':
      return <FormLogo {...rest} key={randomKey('logo')} />;
    case 'title':
      return <FormTitle {...rest} key={randomKey('title')} />;
    case 'subtitle':
      return <FormSubtitle {...rest} key={randomKey('subtitle')} />;
    case 'button':
      const { content, expand, position, justify, ...buttonProps } = rest;
      return (
        <div
          className={classNames(styles.button, position)}
          style={{ flexGrow: expand || 'inherit', justifyContent: justify || "center" }}
          key={randomKey(`button_${buttonProps.type}`)}
        >
          <div>
            <Button {...buttonProps} handleSubmit={handleSubmit}>
              {content}
            </Button>
          </div>
        </div>
      );
    case 'item':
      return matchInput(rest);
    case 'group':
      return (
        <FormGroup direction={rest.direction} className={rest?.className}>
          {renderElements(rest.children, { handleSubmit, errors })}
        </FormGroup>
      );
    case 'message':
      return (
        <FormMessage
          {...rest}
          clearErrors={clearErrors}
          key={randomKey('message')}
        />
      );
    default:
      return null;
  }
};

const Form = props => {
  const { children, elements, onSubmit, classes, ...rest } = props;
  const methods = useForm({ reValidateMode: 'onSubmit' });
  function renderChildren() {
    if (elements) {
      return (
        <>
          {elements.map(e => {
            if (e?.render !== 'outside') return null;
            return renderElements(e, methods);
          })}

          <div className={classNames(styles.container, ...classes)} {...rest}>
            {renderElements(
              elements.filter(e => e?.render !== 'outside'),
              methods,
            )}
          </div>
        </>
      );
    }
    return (
      <form
        className={classNames(...classes)}
        onSubmit={methods.handleSubmit(onSubmit)}
        {...rest}
      >
        {children(methods)}
      </form>
    );
  }
  return <FormProvider {...methods}>{renderChildren()}</FormProvider>;
};

Form.defaultProps = {
  classes: [],
};

Form.propTypes = {
  children: PropTypes.func,
  classes: PropTypes.arrayOf(PropTypes.string),
  onSubmit: PropTypes.func,
  buttons: PropTypes.arrayOf(
    PropTypes.objectOf({
      type: PropTypes.string,
      content: PropTypes.string,
    }),
  ),
};

export default Form;
